import "../../assets/ai-workspace-dialog.css";
import { Dialog } from './dialog';
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { Dropdown } from "primereact/dropdown";
import ReactMarkdown from "react-markdown";
import { Menu } from "primereact/menu";
import { useRef } from "react";
import { useFullscreenDialogParent } from '../../general/use-fullscreen-dialog-parent';

export type AiAssistantMode = 'idle' | 'explanation' | 'fragment_translation' | 'response_translation' | 'selection_explanation';

type AiWorkspaceDialogProps = {
  visible: boolean;
  onHide: () => void;
  parent?: HTMLDivElement | null;

  response: string;
  isLoading: boolean;
  isTranslating?: boolean;
  mode: AiAssistantMode;

  displayedLanguage: string;
  preferredLanguage: string;

  targetLanguage: string;
  onTargetLanguageChange: (language: string) => void;
  availableLanguages: string[];

  onExplain: () => void;
  onTranslateFragment: (targetLang: string) => void;
  onTranslateResponse: (targetLang: string) => void;
  onShowPrompt?: () => void;

  canTranslateResponse?: boolean;
};

export function AiWorkspaceDialog({
  visible,
  onHide,
  parent,
  response,
  isLoading,
  isTranslating = false,
  mode = 'idle',
  displayedLanguage,
  preferredLanguage,
  targetLanguage,
  onTargetLanguageChange,
  availableLanguages,
  onExplain,
  onTranslateFragment,
  onTranslateResponse,
  onShowPrompt,
  canTranslateResponse = false
}: AiWorkspaceDialogProps) {
  const dialogParent = useFullscreenDialogParent(parent);
  
  const getModeLabel = (mode: AiAssistantMode): string => {
    switch (mode) {
        case 'explanation':
          return 'Explaining this content';
        case 'fragment_translation':
            return 'Translating original content';
        case 'response_translation':
            return 'Translating AI explanation';
        case 'selection_explanation':
            return 'Explaining selected text';
        default:
            return 'Ready';
    }
  };

  const languageOptions = availableLanguages.map(language => ({
    label: language,
    value: language
  }));

  const mobileActionsMenuRef = useRef<Menu | null>(null);

  const mobileActionItems = [
    {
        label: "Explain",
        icon: "pi pi-sparkles",
        command: onExplain,
        disabled: isLoading || isTranslating
    },
    {
        label: "Translate fragment",
        icon: "pi pi-language",
        command: () => onTranslateFragment(targetLanguage),
        disabled: isLoading || isTranslating || !targetLanguage || targetLanguage === displayedLanguage
    },
    {
        label: "Translate response",
        icon: "pi pi-language",
        command: () => onTranslateResponse(targetLanguage),
        disabled: !canTranslateResponse || isLoading || isTranslating || !targetLanguage || targetLanguage === displayedLanguage
    },
    ...(onShowPrompt ? [{
        label: "Show prompt",
        icon: "pi pi-code",
        command: onShowPrompt,
        disabled: isLoading || isTranslating
    }] : []),
    {
        label: "Close",
        icon: "pi pi-times",
        command: onHide
    }
  ];

  const footer = (
    <div className="ai-assistant-footer w-full">
        <div className="ai-assistant-footer-desktop">
            <div className="ai-assistant-footer-actions">
                <Button
                label="Explain"
                icon="pi pi-sparkles"
                onClick={onExplain}
                className="p-button-primary"
                disabled={isLoading || isTranslating}
                />

                <Button
                label={`Translate fragment${targetLanguage ? ` (${targetLanguage})` : ""}`}
                icon="pi pi-language"
                onClick={() => onTranslateFragment(targetLanguage)}
                className="p-button-outlined"
                disabled={isLoading || isTranslating || !targetLanguage || targetLanguage === displayedLanguage}
                />

                <Button
                label={`Translate response${targetLanguage ? ` (${targetLanguage})` : ""}`}
                icon="pi pi-language"
                onClick={() => onTranslateResponse(targetLanguage)}
                className="p-button-outlined"
                disabled={!canTranslateResponse || isLoading || isTranslating || !targetLanguage || targetLanguage === displayedLanguage}
                />
            </div>

            <Button
                label="Close"
                icon="pi pi-times"
                onClick={onHide}
                className="p-button-secondary ai-assistant-close-btn"
            />
        </div>

        <div className="ai-assistant-footer-mobile">
            <Menu model={mobileActionItems} popup ref={mobileActionsMenuRef} popupAlignment="left" />
            <Button
                label="Actions"
                icon="pi pi-bars"
                onClick={(e) => mobileActionsMenuRef.current?.toggle(e)}
                className="p-button-primary w-full"
                disabled={isLoading || isTranslating}
            />
        </div>
    </div>
  );

  return (
    <Dialog
      header="AI assistant"
      visible={visible}
      onHide={onHide}
      modal
      draggable={false}
      appendTo={dialogParent}
      className="ai-assistant-dialog"
      style={{
        width: "min(1000px, 95vw)",
        height: "min(85vh, 900px)"
      }}
      breakpoints={{
        "960px": "96vw",
        "640px": "100vw"
      }}
      contentStyle={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        background: "linear-gradient(180deg, #ffffdf, #328f95)"
      }}
      footer={footer}
    >
      <div
        className="flex flex-column gap-3"
        style={{ height: "100%", minHeight: 0 }}
      >
        <div className="ai-assistant-banner">
            🤖 AI assistant is helping you understand this content
        </div>
        <div className="ai-assistant-meta">
          <div className="text-sm text-600">
            Mode: <b>{getModeLabel(mode)}</b>
          </div>

          <div className="text-sm text-600">
            Source: <b>{displayedLanguage}</b>
          </div>

          <div className="flex align-items-center gap-2">
            <span className="text-sm text-600">Target:</span>
            <Dropdown
              appendTo={dialogParent}
              value={targetLanguage}
              options={languageOptions}
              onChange={(e) => onTargetLanguageChange(e.value)}
              placeholder="Select language"
              className="w-12rem p-dropdown"
              disabled={isLoading || isTranslating}
              scrollHeight="500px"
            />
          </div>

          {(isLoading || isTranslating) && (
            <div className="flex align-items-center gap-2">
              <ProgressSpinner
                style={{ width: "24px", height: "24px" }}
                strokeWidth="6"
              />
              <span className="text-sm text-600">
                {isTranslating ? "🤖 Translating..." : "🤖 Thinking..."}
              </span>
            </div>
          )}
        </div>

        <div className="ai-assistant-response">
          {response ? (
            <div className="ai-markdown">
              <ReactMarkdown>{response}</ReactMarkdown>
            </div>
          ) : (
            <div className="text-600">
              Use the buttons below to explain the current fragment or translate it.
            </div>
          )}
        </div>

        {!!onShowPrompt && (
            <div className="ai-assistant-utility-row ai-assistant-utility-row-desktop">
                <Button
                    label="Show prompt"
                    icon="pi pi-code"
                    onClick={onShowPrompt}
                    className="p-button-text p-button-sm"
                    disabled={isLoading || isTranslating}
                />
            </div>
            )}
      </div>
    </Dialog>
  );
}
